import { PolfEventPokerStatusEnum } from '@polf/shared/codegen/types'
import { useLivePoll } from '@polf/shared/hooks'
import { useMemo } from 'react'

import {
  type CurrentPlayerPokerViewQuery,
  useCurrentPlayerPokerViewQuery,
} from '../../../__generated__/hooks'
import {
  bbRatio,
  minutesToBreak,
  type PayJump,
  payJumpFor,
} from '../../../util/poker'

export type PokerView = NonNullable<
  CurrentPlayerPokerViewQuery['currentPlayerPokerView']
>

/**
 * One of the discrete player-facing states. The dispatcher in
 * `pages/PokerSeat/index.tsx` switches on this.
 *
 * `winner` is a special case of `finished` — tournament over, you have
 * `finishingPosition=1`. Rendered with the eliminated layout but with
 * crown/winner identity.
 */
export type PokerState =
  | 'loading'
  | 'error'
  | 'no-poker'
  | 'pre-draw'
  | 'seated'
  | 'active'
  | 'paused'
  | 'eliminated'
  | 'winner'

type Derived = {
  /** Resolved state — drives the view dispatcher. */
  state: PokerState
  /** Your stack expressed in big blinds. Null pre-clock or no stack. */
  myBB: number | null
  /** Average stack across remaining players in BBs. */
  avgBB: number | null
  /**
   * Your rank in the chip-leader board (1 = chip leader). Computed from
   * `activeStandings` sorted by currentStack DESC. Null when the floor
   * hasn't enabled the chip-leader board (server returns empty array) or
   * when you're not in active standings (eliminated / pre-draw).
   */
  myPosition: number | null
  /** Pay-jump intel for SeatCard + BubbleBanner. */
  payJump: PayJump | null
  /** Minutes until first upcoming break, or null. */
  minsToBreak: number | null
  /** Remaining seconds in current level (paused-aware). 0 if no clock. */
  remainingSec: number
  /** True when status is Paused. Lighter than re-checking. */
  isPaused: boolean
  /** True when tournament is FINISHED (regardless of your seat state). */
  tournamentOver: boolean
}

const pokerPollMs = (status: PolfEventPokerStatusEnum | null): number => {
  switch (status) {
    case PolfEventPokerStatusEnum.Running:
    case PolfEventPokerStatusEnum.Paused:
      return 3_000
    case PolfEventPokerStatusEnum.Finished:
      return 60_000
    default:
      return 10_000
  }
}

/**
 * Single source of truth for the poker tab. Owns the query, the 3s poll,
 * and all derived numbers. Views and components consume the result —
 * none of them re-derive (or worse, fetch).
 *
 * @param hasPokerPhase  pulled from `CurrentPlayerCtx` — when false we
 *                       skip the query entirely and return state='no-poker'.
 */
export const usePokerView = (
  hasPokerPhase: boolean
): { view: PokerView | null; loading: boolean; error: unknown } & Derived => {
  const { data, loading, error, refetch } = useCurrentPlayerPokerViewQuery({
    fetchPolicy: 'cache-and-network',
    skip: !hasPokerPhase,
  })
  // Non-overlapping poll instead of Apollo's pollInterval — with up to ~100
  // player clients each polling the poker view, overlapping slow responses
  // were a big part of the pool pile-up. See useLivePoll.
  //
  // 3s buys nothing outside a running clock: before the draw and after the
  // last hand, the only changes are admin-paced. A phone left on this tab
  // untouched steps down further and snaps back on the first touch.
  const liveStatus = data?.currentPlayerPokerView?.status ?? null
  useLivePoll(refetch, pokerPollMs(liveStatus), hasPokerPhase, {
    idleAfterMs: 5 * 60_000,
    idleIntervalMs: 15_000,
  })

  // Note: useMemo runs every render but the result is referentially
  // stable when inputs don't change. Components downstream rely on
  // that for their own memos.
  return useMemo(() => {
    if (!hasPokerPhase) {
      return emptyDerived('no-poker', null, false, error)
    }
    if (loading && !data) {
      return emptyDerived('loading', null, true, error)
    }
    const view = data?.currentPlayerPokerView ?? null
    // Only a MISSING view is an error. A transient poll failure must NOT blank
    // a seat we already have: this query polls every 3s on cache-and-network,
    // and the golf→poker handoff (or any blip) can momentarily error while
    // Apollo still holds the last good `data`. Falling through on `error` when
    // `view` exists keeps the seat on screen until the next poll reconciles —
    // mirrors PlayerShell's guard. Blanking on `error` here is what flashed
    // "Kunne ikke laste pokerstatus" a few seconds after seating.
    if (!view) {
      return emptyDerived('error', null, false, error)
    }

    const status = view.status ?? PolfEventPokerStatusEnum.NotStarted
    const isPaused = status === PolfEventPokerStatusEnum.Paused
    const tournamentOver = status === PolfEventPokerStatusEnum.Finished

    // State resolution. Order matters — eliminated wins over status flags.
    let state: PokerState
    if (status === PolfEventPokerStatusEnum.NotStarted || !view.seat) {
      state = 'pre-draw'
    } else if (view.seat.eliminated) {
      // Eliminated (lost or finished-not-1st). Winner detected separately.
      state = 'eliminated'
    } else if (tournamentOver) {
      // Tournament finished AND you weren't eliminated → you won.
      state = 'winner'
    } else if (status === PolfEventPokerStatusEnum.Seated) {
      state = 'seated'
    } else if (isPaused) {
      state = 'paused'
    } else {
      state = 'active'
    }

    // Remaining-seconds derivation (paused-aware) — drives the clock and
    // minsToBreak. We don't tick here (would re-render every 500ms across
    // every consumer); components that need smooth countdown run their own
    // local tick. This value is the "snapshot at last poll" baseline.
    const startedAt = view.currentLevelStartedAt
      ? new Date(view.currentLevelStartedAt).getTime()
      : null
    const pausedAt = view.currentLevelPausedAt
      ? new Date(view.currentLevelPausedAt).getTime()
      : null
    const effectiveNow = pausedAt ?? Date.now()
    const durSec = (view.currentLevel?.durationMinutes ?? 0) * 60
    const remainingSec =
      view.currentLevel && startedAt
        ? Math.max(0, durSec - (effectiveNow - startedAt) / 1000)
        : durSec

    const bb = view.currentLevel?.bigBlind ?? null
    const myBB = bbRatio(view.seat?.currentStack, bb)
    const avgBB = bbRatio(view.averageStack, bb)

    // My rank within the chip-leader board. Server orders activeStandings
    // by currentStack DESC, but we re-sort defensively in case the order
    // ever ships up wrong. `findIndex` returns -1 when I'm not active.
    let myPosition: number | null = null
    if (view.seat?.id && view.activeStandings.length > 0) {
      const sorted = [...view.activeStandings].sort(
        (a, b) => b.currentStack - a.currentStack
      )
      const idx = sorted.findIndex((s) => s.id === view.seat?.id)
      if (idx >= 0) myPosition = idx + 1
    }

    const payJump = payJumpFor({
      playersLeft: view.playersLeft,
      payouts: view.payouts,
      prizePool: view.prizePool,
      usePayouts: view.usePayouts,
    })

    const minsToBreak = minutesToBreak({
      currentLevel: view.currentLevel,
      inBreak: view.currentInBreak ?? false,
      remainingSec,
    })

    return {
      view,
      loading,
      error,
      state,
      myBB,
      avgBB,
      myPosition,
      payJump,
      minsToBreak,
      remainingSec,
      isPaused,
      tournamentOver,
    }
  }, [data, error, loading, hasPokerPhase])
}

const emptyDerived = (
  state: PokerState,
  view: PokerView | null,
  loading: boolean,
  error: unknown
): { view: PokerView | null; loading: boolean; error: unknown } & Derived => ({
  view,
  loading,
  error,
  state,
  myBB: null,
  avgBB: null,
  myPosition: null,
  payJump: null,
  minsToBreak: null,
  remainingSec: 0,
  isPaused: false,
  tournamentOver: false,
})
